import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog } from '../ui/components/Dialog';
import { Button } from '../ui/components/Button';
import { TextField } from '../ui/components/TextField';
import { useEmailConfirmation } from '../hooks/useEmailConfirmation';
import { isFirstLogin } from '../api/settings';
import { FeatherMail, FeatherX } from '@subframe/core';

interface EmailCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSkip: () => void;
}

export const EmailCollectionModal: React.FC<EmailCollectionModalProps> = ({
  isOpen,
  onClose,
  onSkip,
}) => {
  const [email, setEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(false);
  const hasHandledSuccessRef = useRef(false);

  const {
    setEmail: submitEmail,
    isSettingEmail,
    setEmailError,
    setEmailSuccess,
  } = useEmailConfirmation();

  // Validate email format
  useEffect(() => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValidEmail(emailRegex.test(email));
  }, [email]);

  // Reset the success handler ref when modal opens
  useEffect(() => {
    if (isOpen) {
      hasHandledSuccessRef.current = false;
    }
  }, [isOpen]);

  // Handle successful email submission - use ref to prevent multiple calls
  useEffect(() => {
    if (setEmailSuccess && !hasHandledSuccessRef.current) {
      hasHandledSuccessRef.current = true;
      // Use setTimeout to defer the state update to the next tick
      // This prevents the "Maximum update depth exceeded" error
      setTimeout(() => {
        onClose();
      }, 0);
    }
  }, [setEmailSuccess, onClose]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValidEmail && email) {
      submitEmail(email);
    }
  };

  const handleSkip = () => {
    setEmail('');
    onSkip();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleSkip()}>
      <Dialog.Content className="w-full max-w-[480px]">
        <form onSubmit={handleSubmit} className="flex w-full flex-col items-start gap-6 p-6">
          {/* Header */}
          <div className="flex w-full items-start justify-between">
            <div className="flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <FeatherMail className="h-6 w-6 text-brand-600" />
                <span className="text-heading-2 font-heading-2 text-default-font">
                  Add your email
                </span>
              </div>
              <span className="text-body font-body text-subtext-color">
                Stay updated with notifications about your NFTs and marketplace activity
              </span>
            </div>
            <button
              type="button"
              onClick={handleSkip}
              className="flex h-8 w-8 items-center justify-center rounded hover:bg-neutral-100"
            >
              <FeatherX className="h-5 w-5 text-subtext-color" />
            </button>
          </div>

          {/* Error Message */}
          {setEmailError && (
            <div className="w-full rounded border border-red-200 bg-red-50 px-4 py-3 text-body font-body text-red-700">
              Failed to add email: {setEmailError.message}
            </div>
          )}

          {/* Email Input */}
          <TextField
            className="h-auto w-full flex-none"
            label="Email address"
            helpText="We'll send you a confirmation email"
          >
            <TextField.Input
              placeholder="your@email.com"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              autoFocus
            />
          </TextField>

          {/* Benefits List */}
          <div className="flex w-full flex-col items-start gap-3">
            <span className="text-body-bold font-body-bold text-default-font">
              Why add your email?
            </span>
            <ul className="flex flex-col gap-2 text-body font-body text-subtext-color">
              <li className="flex items-start gap-2">
                <span className="text-brand-600">•</span>
                <span>Get notified when you receive offers on your NFTs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-600">•</span>
                <span>Receive updates about collections you're watching</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-brand-600">•</span>
                <span>Stay informed about important marketplace announcements</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex w-full items-center justify-between gap-3">
            <Button
              variant="neutral-tertiary"
              type="button"
              onClick={handleSkip}
              disabled={isSettingEmail}
            >
              Skip for now
            </Button>
            <Button
              size="large"
              type="submit"
              disabled={!isValidEmail || isSettingEmail}
            >
              {isSettingEmail ? 'Adding email...' : 'Add email address'}
            </Button>
          </div>

          {/* Privacy Note */}
          <span className="text-caption font-caption text-subtext-color">
            We'll never share your email address with third parties. You can update your
            email preferences anytime in settings.
          </span>
        </form>
      </Dialog.Content>
    </Dialog>
  );
};

/**
 * Hook to manage the email collection modal state
 * Automatically shows the modal on first login
 * Uses authStore for persistent state management
 */
export const useEmailCollectionModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);

  // Import authStore dynamically to avoid circular dependency
  const [authStore, setAuthStore] = useState<any>(null);

  useEffect(() => {
    // Dynamically import authStore
    import('../store/authStore').then(module => {
      setAuthStore(module.useAuthStore.getState());
    });
  }, []);

  useEffect(() => {
    const checkFirstLogin = async () => {
      if (hasChecked || !authStore) return;

      // Don't show if already dismissed
      if (authStore.emailModalDismissed) {
        setHasChecked(true);
        return;
      }

      try {
        const result = await isFirstLogin();
        if (result.is_first_login) {
          setIsOpen(true);
        }
        setHasChecked(true);
      } catch (error) {
        console.error('Failed to check first login status:', error);
        setHasChecked(true);
      }
    };

    checkFirstLogin();
  }, [hasChecked, authStore]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (authStore) {
      authStore.dismissEmailModal();
    }
  }, [authStore]);

  const handleSkip = useCallback(() => {
    setIsOpen(false);
    if (authStore) {
      authStore.dismissEmailModal();
    }
  }, [authStore]);

  return {
    isOpen,
    handleClose,
    handleSkip,
  };
};
